import { createContext, useContext, type ReactElement, type ReactNode } from "react";

import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "#/components/ui/popover.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "#/components/ui/tooltip.tsx";
import { useCoarsePointer } from "#/hooks/useCoarsePointer.ts";
import { cn } from "#/lib/utils.ts";

const CoarsePointerContext = createContext(false);

const infotipPopupClassName =
  "inline-flex w-fit max-w-xs flex-row items-center gap-0 rounded-md bg-foreground px-3 py-1.5 text-xs text-background shadow-none ring-0";

const infotipArrowClassName = "bg-foreground fill-foreground";

function InfotipProvider({ delay = 0, children }: { delay?: number; children: ReactNode }) {
  const coarse = useCoarsePointer();
  const tree = coarse ? children : <TooltipProvider delay={delay}>{children}</TooltipProvider>;

  return <CoarsePointerContext.Provider value={coarse}>{tree}</CoarsePointerContext.Provider>;
}

function useIsCoarsePointer(): boolean {
  return useContext(CoarsePointerContext);
}

function Infotip({ children }: { children: ReactNode }) {
  const coarse = useIsCoarsePointer();

  if (coarse) {
    return (
      <Popover modal={false} data-slot="infotip">
        {children}
      </Popover>
    );
  }

  return <Tooltip data-slot="infotip">{children}</Tooltip>;
}

type InfotipTriggerProps = {
  "aria-label"?: string;
  children?: ReactNode;
  className?: string;
  delay?: number;
  render?: ReactElement;
};

function InfotipTrigger({ className, delay, ...props }: InfotipTriggerProps) {
  const coarse = useIsCoarsePointer();

  if (coarse) {
    return (
      <PopoverTrigger
        data-slot="infotip-trigger"
        openOnHover
        delay={delay}
        className={cn("outline-none focus-visible:outline-none", className)}
        nativeButton={false}
        {...props}
      />
    );
  }

  return <TooltipTrigger data-slot="infotip-trigger" className={className} {...props} />;
}

type InfotipContentProps = {
  align?: "start" | "center" | "end";
  alignOffset?: number;
  children?: ReactNode;
  className?: string;
  side?: "top" | "bottom" | "left" | "right" | "inline-start" | "inline-end";
  sideOffset?: number;
};

function InfotipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  children,
}: InfotipContentProps) {
  const coarse = useIsCoarsePointer();

  if (coarse) {
    return (
      <PopoverContent
        data-slot="infotip-content"
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        positionMethod="fixed"
        className={cn(infotipPopupClassName, className)}
      >
        {children}
        <PopoverArrow className={infotipArrowClassName} />
      </PopoverContent>
    );
  }

  return (
    <TooltipContent
      data-slot="infotip-content"
      className={className}
      side={side}
      sideOffset={sideOffset}
      align={align}
      alignOffset={alignOffset}
    >
      {children}
    </TooltipContent>
  );
}

export { Infotip, InfotipContent, InfotipProvider, InfotipTrigger };
